import { useEffect, useMemo } from 'react';
import { MapPin } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocation, type Location } from '@/contexts/LocationContext';
import { cn } from '@/lib/utils';
import { getProductionCentres } from '@/features/production-orders/utils/production-centres';

interface LocationFilterProps {
  className?: string;
  showAllOption?: boolean;
  /** Limit options to active locations that operate the production workflow. */
  productionOnly?: boolean;
}

/**
 * Reusable location filter dropdown.
 * Reads/writes selectedLocation from LocationContext for backward compatibility.
 * When "All Locations" is selected, sets selectedLocation to null.
 *
 * BLU-367: Locations reached via active 3PL junction (`is_shared === true`)
 * are grouped under their owner org with a "(3PL)" suffix. Owned locations
 * appear first in the legacy "Your locations" group. With no shared
 * locations the dropdown looks identical to the pre-BLU-367 single-list
 * shape — no visual regression for single-tenant users.
 */
export function LocationFilter({ className, showAllOption = true, productionOnly = false }: LocationFilterProps) {
  const { selectedLocation, setSelectedLocation, locations, isLoading } = useLocation();

  const visibleLocations = useMemo(
    () => (productionOnly ? getProductionCentres(locations) : locations),
    [locations, productionOnly],
  );
  const groups = useMemo(() => groupLocationsByOwner(visibleLocations), [visibleLocations]);
  const hasSharedGroups = groups.length > 1;

  // A location may remain selected in localStorage after production is turned
  // off for that site. Clear it once locations have loaded so production pages
  // cannot continue querying an inventory-only floor.
  useEffect(() => {
    if (
      productionOnly &&
      !isLoading &&
      selectedLocation &&
      !visibleLocations.some((location) => location.id === selectedLocation.id)
    ) {
      setSelectedLocation(null);
    }
  }, [isLoading, productionOnly, selectedLocation, setSelectedLocation, visibleLocations]);

  if (isLoading) return <Skeleton className={cn('h-10 w-[16rem] min-w-0 md:w-[18rem]', className)} />;
  if (visibleLocations.length <= 1 && !showAllOption) return null;

  const handleChange = (value: string) => {
    if (value === '__all__') {
      setSelectedLocation(null);
      return;
    }
    const loc = visibleLocations.find((l) => l.id === value);
    if (loc) {
      setSelectedLocation(loc);
    }
  };

  return (
    <Select
      value={selectedLocation?.id ?? '__all__'}
      onValueChange={handleChange}
    >
      <SelectTrigger
        className={cn('w-[16rem] min-w-0 md:w-[18rem]', className)}
        aria-label="Select location"
      >
        <div className="flex min-w-0 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          {/* Radix's SelectValue drops `className`, so the truncate lives on a
              wrapper: a long name ends in an ellipsis on one line instead of
              wrapping out of the 40px trigger (full name in the list). */}
          <span className="min-w-0 truncate">
            <SelectValue placeholder="All Locations" />
          </span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {showAllOption && (
          <SelectItem value="__all__">All Locations</SelectItem>
        )}
        {hasSharedGroups
          ? groups.map((group) => (
              <SelectGroup key={group.key}>
                <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </SelectLabel>
                {group.locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          : visibleLocations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}

interface LocationGroup {
  key: string;
  label: string;
  locations: Location[];
}

/**
 * Group locations by owner org. Caller's own locations come first as "Your
 * locations"; shared (3PL) locations group by owner_organization_id with a
 * "(3PL)" suffix. We don't have owner-org NAMES in LocationContext yet — the
 * label uses the owner_organization_id prefix as a stable key. A future
 * extension can fetch owner-org display names alongside listLocations.
 */
function groupLocationsByOwner(locations: Location[]): LocationGroup[] {
  const owned: Location[] = [];
  const sharedByOwner = new Map<string, Location[]>();

  for (const loc of locations) {
    if (loc.is_shared && loc.owner_organization_id) {
      const list = sharedByOwner.get(loc.owner_organization_id) ?? [];
      list.push(loc);
      sharedByOwner.set(loc.owner_organization_id, list);
    } else {
      owned.push(loc);
    }
  }

  const groups: LocationGroup[] = [];
  if (owned.length > 0) {
    groups.push({ key: 'owned', label: 'Your locations', locations: owned });
  }
  // BLU-367 F.8: alphabetise partner orgs by name so the dropdown is
  // deterministic regardless of junction insertion order. Owned-first is
  // already enforced by the conditional push above.
  const partnerEntries = Array.from(sharedByOwner.entries()).map(([ownerId, locs]) => ({
    ownerId,
    ownerName: locs[0]?.owner_organization_name?.trim() ?? '',
    locs,
  }));
  partnerEntries.sort((a, b) => a.ownerName.localeCompare(b.ownerName));
  for (const { ownerId, ownerName, locs } of partnerEntries) {
    groups.push({
      key: `shared-${ownerId}`,
      label: ownerName ? `3PL: ${ownerName}` : '3PL partner',
      locations: locs,
    });
  }
  return groups;
}
